// The Reports screen (spec/13 §2, §15; spec/17 §4.1, §4.2; F-ANL-03): a range of business days, a scope, and tabs —
//
//   Overview              revenue, money in, sessions, occupancy, average session, revenue per player; trend; peaks
//   Revenue               by day or week (paid directly / from wallets), by category, by tender, money in → revenue
//   Sessions & occupancy  the hour × weekday heatmap, peaks, by hour, by class and zone
//   Products              top food and drink, passes sold, play by passes and rate card
//   Passes & wallet       top-ups (a liability) vs wallet spend (revenue) vs balances owed, bonus cost, pass liability
//   Staff                 sales, top-ups, refunds, discounts, voids and comps by member
//   Games played          titles by hours, by day or week, by PC (F-CAT-04)
//   Daily summary         one business day as owners get it by email (F-ANL-07) — owners only
//
// **The range** is a preset — resolved by the server in the venue's timezone and business-day cutoff, so "Today" at
// 02:00 is still last night's business day — or a custom range of business days; every headline figure is compared
// with the previous period of the same length. **The scope** is this venue, every venue the member works at (rolled
// up), or another venue. Tab, range and scope live in the URL (`report-search.ts`): a report is a link to share. The
// Daily summary reads one business day (`?day=`) instead of a range; "all venues" is the organisation's summary.
//
// `reports:read` opens the screen (Sessions & occupancy, Games played, Overview's sessions half); `reports:financial`
// adds every money figure; the Daily summary is an owner's alone. The server checks each again; this only decides what
// to offer.

import type { MyVenue } from '../../api/me';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Field, Input, Select, Tabs, useToast } from '@clavian/ui-web';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { useAccess, useMyVenues } from '../../api/queries';
import { useCurrentVenue } from '../../shell/scope';
import { Forbidden, Page, TableSkeleton, Toolbar } from '../../ui';
import { DailySummaryTab } from './DailySummaryTab';
import { GamesPlayedTab } from './GamesPlayedTab';
import { OverviewHero } from './OverviewHero';
import { OverviewTab } from './OverviewTab';
import { ProductsTab } from './ProductsTab';
import { type ReportScope, useReportRange, useSendDailySummary } from './queries';
import { nextSearch, rangeChoiceOf, readReportSearch, REPORT_PRESETS, REPORT_RANGE_MAX_DAYS, type ReportSearch, type ReportTab } from './report-search';
import { RevenueTab } from './RevenueTab';
import { SessionsTab } from './SessionsTab';
import { StaffTab } from './StaffTab';
import { rangeLabel } from './tab-kit';
import { WalletTab } from './WalletTab';

import './reports.css';

const REPORTS_READ = 'reports:read';
const REPORTS_FINANCIAL = 'reports:financial';

/** The tabs a role reads: the money tabs need `reports:financial`; the Daily summary is the owner's too. */
const TAB_NEEDS: Readonly<Record<ReportTab, 'read' | 'financial' | 'owner'>> = {
  overview: 'read',
  revenue: 'financial',
  sessions: 'read',
  products: 'financial',
  wallet: 'financial',
  staff: 'financial',
  games: 'read',
  daily: 'owner',
};

export function ReportsPage(): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const navigate = useNavigate();
  const search = readReportSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const access = useAccess({ venueId: venue.id });
  const ready = access.status === 'ready';
  const canRead = ready && (access.can(REPORTS_READ) || access.can(REPORTS_FINANCIAL));
  const canFinancial = ready && access.can(REPORTS_FINANCIAL);
  const isOwner = ready && access.role === 'owner';
  const myVenues = useMyVenues();
  const orgVenues: readonly MyVenue[] = useMemo(() => (myVenues.data ?? []).filter((one) => one.organizationId === venue.organizationId), [myVenues.data, venue.organizationId]);
  const sendSummary = useSendDailySummary(venue.organizationId);

  const setSearch = (patch: Partial<Record<keyof ReportSearch, string | undefined>>) =>
    void navigate({ to: '/v/$venueSlug/reports', params: { venueSlug: venue.slug }, search: nextSearch(search, patch), replace: true });

  // The scope: this venue (no key), every venue (`all`), or one named venue the member works at.
  const named = search.venue !== undefined && search.venue !== 'all' ? orgVenues.find((one) => one.id === search.venue) : undefined;
  const scopeVenueId = search.venue === 'all' ? undefined : (named?.id ?? venue.id);
  const rolledUp = search.venue === 'all';

  const allowed = (tab: ReportTab) => {
    const needs = TAB_NEEDS[tab];
    return needs === 'owner' ? canFinancial && isOwner : needs === 'financial' ? canFinancial : canRead;
  };
  const tab: ReportTab = search.tab !== undefined && allowed(search.tab) ? search.tab : 'overview';
  const daily = tab === 'daily';

  const choice = rangeChoiceOf(search);
  const resolved = useReportRange(venue.organizationId, choice.kind === 'preset' ? choice.preset : undefined, scopeVenueId ?? venue.id, canRead && !daily);
  const days =
    choice.kind === 'custom'
      ? choice
      : choice.kind === 'preset' && resolved.data !== undefined
        ? { from: resolved.data.from, to: resolved.data.to, previousFrom: resolved.data.previousFrom, previousTo: resolved.data.previousTo }
        : undefined;

  /** "Email it to me": the summary in view, to the owner's own verified address. */
  const emailIt = () =>
    sendSummary.mutate(
      { ...(search.day === undefined ? {} : { day: search.day }), ...(rolledUp || scopeVenueId === undefined ? {} : { venueId: scopeVenueId }) },
      {
        onSuccess: () => show({ tone: 'ok', title: t('reports.daily.emailed') }),
        onError: (error) =>
          show({ tone: 'danger', title: problemCode(error) === PROBLEM.emailNotVerified ? t('reports.daily.emailUnverified') : problem.text(error, { fallback: t('reports.daily.emailFailed') }) }),
      },
    );

  let body: ReactNode;
  if (!ready) body = <TableSkeleton />;
  else if (!canRead) body = <Forbidden message={t('reports.notPermitted')} />;
  else if (!daily && choice.kind === 'invalid') body = <p className="console-subtle">{t('reports.range.invalid', { max: REPORT_RANGE_MAX_DAYS })}</p>;
  else if (!daily && days === undefined) body = <TableSkeleton />;
  else {
    const scope: ReportScope | undefined = days === undefined ? undefined : { organizationId: venue.organizationId, venueId: scopeVenueId, from: days.from, to: days.to };
    const previousLabel = days === undefined ? '' : rangeLabel(format, days.previousFrom, days.previousTo);
    const content = (which: ReportTab): ReactNode => {
      if (which !== tab) return null;
      if (which === 'daily') return <DailySummaryTab organizationId={venue.organizationId} venueId={rolledUp ? undefined : scopeVenueId} day={search.day} />;
      if (scope === undefined || days === undefined) return null;
      switch (which) {
        case 'overview':
          return <OverviewTab scope={scope} previousLabel={previousLabel} canFinancial={canFinancial} />;
        case 'revenue':
          return <RevenueTab scope={scope} previousLabel={previousLabel} />;
        case 'sessions':
          return <SessionsTab scope={scope} previousLabel={previousLabel} />;
        case 'products':
          return <ProductsTab scope={scope} />;
        case 'wallet':
          return <WalletTab scope={scope} previousLabel={previousLabel} />;
        case 'staff':
          return <StaffTab scope={scope} />;
        case 'games':
          return <GamesPlayedTab organizationId={venue.organizationId} venueId={scopeVenueId ?? venue.id} from={days.from} to={days.to} rolledUp={rolledUp} />;
      }
    };
    body = (
      <>
        <p className="console-rt-summary console-subtle">
          {daily || days === undefined
            ? t('reports.daily.intro')
            : `${t('reports.range.summary', { range: rangeLabel(format, days.from, days.to), previous: previousLabel })}${
                resolved.data !== undefined && choice.kind === 'preset' ? ` ${t('reports.range.cutoff', { cutoff: resolved.data.businessDayCutoff })}` : ''
              }`}
        </p>
        <Tabs
          label={t('reports.tabs')}
          value={tab}
          onValueChange={(value) => setSearch({ tab: value })}
          items={(Object.keys(TAB_NEEDS) as ReportTab[]).filter(allowed).map((value) => ({ value, label: t(`reports.tab.${value}`), content: content(value) }))}
        />
      </>
    );
  }

  const presetValue = search.range ?? 'last7';
  // The figures at the head are the range's headline, read from the reports the Overview tab reads.
  const heroScope: ReportScope | undefined =
    canRead && !daily && days !== undefined ? { organizationId: venue.organizationId, venueId: scopeVenueId, from: days.from, to: days.to } : undefined;
  return (
    <Page
      title={t('reports.title')}
      description={t('reports.intro')}
      hero={heroScope === undefined ? {} : { stats: <OverviewHero scope={heroScope} canFinancial={canFinancial} /> }}
    >
      <Toolbar label={t('reports.toolbar')} className="console-rt-toolbar">
        {daily ? (
          <>
            <Field label={t('reports.daily.day')} className="console-rt-date console-ds-day">
              {(control) => <Input {...control} type="date" value={search.day ?? ''} onChange={(event) => setSearch({ day: event.target.value || undefined })} />}
            </Field>
          </>
        ) : (
          <>
            <div className="console-rt-control">
              <Select
                aria-label={t('reports.range.label')}
                value={presetValue}
                onValueChange={(value) =>
                  value === 'custom'
                    ? setSearch({ range: 'custom', from: search.from ?? days?.from, to: search.to ?? days?.to })
                    : setSearch({ range: value, from: undefined, to: undefined })
                }
                options={[...REPORT_PRESETS.map((value) => ({ value, label: t(`reports.range.${value}`) })), { value: 'custom', label: t('reports.range.custom') }]}
              />
            </div>
            {search.range === 'custom' ? (
              <>
                <Field label={t('reports.range.from')} className="console-rt-date">
                  {(control) => <Input {...control} type="date" value={search.from ?? ''} max={search.to} onChange={(event) => setSearch({ from: event.target.value || undefined })} />}
                </Field>
                <Field label={t('reports.range.to')} className="console-rt-date">
                  {(control) => <Input {...control} type="date" value={search.to ?? ''} min={search.from} onChange={(event) => setSearch({ to: event.target.value || undefined })} />}
                </Field>
              </>
            ) : null}
          </>
        )}
        {orgVenues.length > 1 ? (
          <div className="console-rt-control">
            <Select
              aria-label={t('reports.scope.label')}
              value={search.venue ?? 'here'}
              onValueChange={(value) => setSearch({ venue: value === 'here' ? undefined : value })}
              options={[
                { value: 'here', label: t('reports.scope.here', { venue: venue.name }) },
                { value: 'all', label: t('reports.scope.all', { count: orgVenues.length }) },
                ...orgVenues.filter((one) => one.id !== venue.id).map((one) => ({ value: one.id, label: one.name })),
              ]}
            />
          </div>
        ) : null}
        {daily ? (
          <Button variant="secondary" busy={sendSummary.isPending} onClick={emailIt}>
            {t('reports.daily.email')}
          </Button>
        ) : null}
      </Toolbar>
      {body}
    </Page>
  );
}
